import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCoins,
  IconEdit,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUpload,
  IconUser,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useCustomers, useDeleteCustomer } from './customersApi';
import { CustomerFormDrawer } from './CustomerFormDrawer';
import { AdjustPointsModal } from './AdjustPointsModal';
import { CustomerImportModal } from './CustomerImportModal';
import type { CustomerDto } from './types';

export function CustomersPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { data: rows, isLoading } = useCustomers(search);
  const deleteMutation = useDeleteCustomer();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerDto | null>(null);
  const [deleting, setDeleting] = useState<CustomerDto | null>(null);
  const [pointsFor, setPointsFor] = useState<CustomerDto | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (c: CustomerDto) => {
    setEditing(c);
    setDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.customerId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.fullName,
      });
      setDeleting(null);
    } catch {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: t('common.toasts.errorBody'),
      });
    }
  };

  const hasRows = (rows?.length ?? 0) > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.organization')}
        title={t('customers.page.title')}
        subtitle={t('customers.page.subtitle')}
        actions={
          <Group gap="sm">
            <Button variant="default" leftSection={<IconUpload size={16} stroke={1.8} />}
              onClick={() => setImportOpen(true)}>
              {t('customers.import.button')}
            </Button>
            <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>
              {t('customers.page.create')}
            </Button>
          </Group>
        }
      />

      <Paper p="md">
        <TextInput
          placeholder={t('customers.page.searchPlaceholder')}
          leftSection={<IconSearch size={16} stroke={1.6} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </Paper>

      <Paper p={hasRows ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !hasRows ? (
          <EmptyState
            icon={<IconUser size={22} stroke={1.6} />}
            title={t('customers.empty.title')}
            description={t('customers.empty.body')}
            actionLabel={t('customers.page.create')}
            onAction={openCreate}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('customers.columns.fullName')}</Table.Th>
                <Table.Th>{t('customers.columns.phone')}</Table.Th>
                <Table.Th>{t('customers.columns.email')}</Table.Th>
                <Table.Th>{t('customers.columns.points')}</Table.Th>
                <Table.Th>{t('customers.columns.status')}</Table.Th>
                <Table.Th style={{ width: 140, textAlign: 'end' }}>
                  {t('common.actions')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows!.map((c) => (
                <Table.Tr key={c.customerId}>
                  <Table.Td fw={500}>{c.fullName}</Table.Td>
                  <Table.Td className="font-mono" dir="ltr">
                    {c.phone}
                  </Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)" fz="xs" dir="ltr">
                    {c.email ?? '—'}
                  </Table.Td>
                  <Table.Td className="font-numeric">
                    <Text size="sm" fw={600}>
                      {c.pointsBalance}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {c.isActive ? (
                      <Badge color="green" variant="light" radius="sm">
                        {t('common.active')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="sm">
                        {t('common.inactive')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('customers.points.title')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => setPointsFor(c)}
                          aria-label={t('customers.points.title')}
                        >
                          <IconCoins size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => openEdit(c)}
                          aria-label={t('common.edit')}
                        >
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleting(c)}
                          aria-label={t('common.delete')}
                        >
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <CustomerFormDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        customer={editing}
      />
      <AdjustPointsModal
        opened={pointsFor !== null}
        onClose={() => setPointsFor(null)}
        customer={pointsFor}
      />
      <CustomerImportModal opened={importOpen} onClose={() => setImportOpen(false)} />
      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('customers.delete.title')}
        message={t('customers.delete.body', { name: deleting?.fullName ?? '' })}
        loading={deleteMutation.isPending}
      />
    </Stack>
  );
}
